import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'
import {
  api,
  setTokens,
  getTokens,
  clearTokens,
  type Developer,
  type LoginData,
  type RegisterData,
  type MFAVerifyData,
  ApiError,
} from '../api/client'

interface AuthState {
  developer: Developer | null
  isLoading: boolean
  isAuthenticated: boolean
  error: string | null
  requiresMFA: boolean
  mfaToken: string | null
}

interface AuthContextValue extends AuthState {
  login: (data: LoginData) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => Promise<void>
  verifyMFA: (code: string) => Promise<void>
  clearError: () => void
  refreshDeveloper: () => Promise<void>
  setTokens: (accessToken: string, refreshToken: string, sessionToken?: string) => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    developer: null,
    isLoading: true,
    isAuthenticated: false,
    error: null,
    requiresMFA: false,
    mfaToken: null,
  })

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const { accessToken } = getTokens()
      if (!accessToken) {
        setState((prev) => ({ ...prev, isLoading: false }))
        return
      }

      try {
        const developer = await api.me.get()
        setState({
          developer,
          isLoading: false,
          isAuthenticated: true,
          error: null,
          requiresMFA: false,
          mfaToken: null,
        })
      } catch {
        // Token invalid or expired
        clearTokens()
        setState({
          developer: null,
          isLoading: false,
          isAuthenticated: false,
          error: null,
          requiresMFA: false,
          mfaToken: null,
        })
      }
    }

    checkAuth()
  }, [])

  const login = useCallback(async (data: LoginData) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }))

    try {
      const response = await api.auth.login(data)

      if (response.requires_mfa && response.mfa_token) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          requiresMFA: true,
          mfaToken: response.mfa_token ?? null,
        }))
        return
      }

      setTokens(response.access_token, response.refresh_token)
      setState({
        developer: response.developer,
        isLoading: false,
        isAuthenticated: true,
        error: null,
        requiresMFA: false,
        mfaToken: null,
      })
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Login failed'
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }))
      throw err
    }
  }, [])

  const register = useCallback(async (data: RegisterData) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }))

    try {
      const response = await api.auth.register(data)
      setTokens(response.access_token, response.refresh_token)
      setState({
        developer: response.developer,
        isLoading: false,
        isAuthenticated: true,
        error: null,
        requiresMFA: false,
        mfaToken: null,
      })
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Registration failed'
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }))
      throw err
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await api.auth.logout()
    } catch {
      // Ignore logout errors
    } finally {
      clearTokens()
      setState({
        developer: null,
        isLoading: false,
        isAuthenticated: false,
        error: null,
        requiresMFA: false,
        mfaToken: null,
      })
    }
  }, [])

  const verifyMFA = useCallback(
    async (code: string) => {
      if (!state.mfaToken) {
        throw new Error('No MFA token available')
      }

      setState((prev) => ({ ...prev, isLoading: true, error: null }))

      try {
        const data: MFAVerifyData = {
          mfa_token: state.mfaToken,
          code,
        }
        const response = await api.auth.verifyMFA(data)
        setTokens(response.access_token, response.refresh_token)
        setState({
          developer: response.developer,
          isLoading: false,
          isAuthenticated: true,
          error: null,
          requiresMFA: false,
          mfaToken: null,
        })
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'MFA verification failed'
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: message,
        }))
        throw err
      }
    },
    [state.mfaToken]
  )

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }))
  }, [])

  const refreshDeveloper = useCallback(async () => {
    try {
      const developer = await api.me.get()
      setState((prev) => ({ ...prev, developer }))
    } catch {
      // Ignore errors
    }
  }, [])

  // Set tokens from OAuth callback
  const setTokensCallback = useCallback(async (accessToken: string, refreshToken: string, _sessionToken?: string) => {
    setState((prev) => ({ ...prev, isLoading: true }))

    // Store tokens
    setTokens(accessToken, refreshToken)

    try {
      // Fetch developer profile
      const developer = await api.me.get()
      setState({
        developer,
        isLoading: false,
        isAuthenticated: true,
        error: null,
        requiresMFA: false,
        mfaToken: null,
      })
    } catch (err) {
      clearTokens()
      const message = err instanceof ApiError ? err.message : 'Authentication failed'
      setState({
        developer: null,
        isLoading: false,
        isAuthenticated: false,
        error: message,
        requiresMFA: false,
        mfaToken: null,
      })
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        register,
        logout,
        verifyMFA,
        clearError,
        refreshDeveloper,
        setTokens: setTokensCallback,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
